import { Paper, Typography, Box, Stack, useTheme } from "@mui/material";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import AirIcon from "@mui/icons-material/Air";
import { tokens } from "../../../theme";
import type { OpsSmartAlert } from "../types/opsDashboard";

function alertStyle(alert: OpsSmartAlert): {
  accentColor: string;
  tintBg: string;
  icon: React.ReactNode;
} {
  const w = alert.weather.toLowerCase();
  if (w.includes("thunder") || alert.severity === "warning") {
    return {
      accentColor: "#ef4444",
      tintBg: "#fef2f2",
      icon: <ThunderstormIcon sx={{ color: "#ef4444" }} />,
    };
  }
  if (w.includes("rain") || w.includes("drizzle")) {
    return {
      accentColor: "#3a86ff",
      tintBg: "#edf4fe",
      icon: <WaterDropIcon sx={{ color: "#3a86ff" }} />,
    };
  }
  if (w.includes("fog") || w.includes("mist") || w.includes("haze")) {
    return {
      accentColor: "#6b7280",
      tintBg: "#f3f4f6",
      icon: <AirIcon sx={{ color: "#6b7280" }} />,
    };
  }
  return {
    accentColor: "#ffa200",
    tintBg: "#fff9eb",
    icon: <CloudQueueIcon sx={{ color: "#ffa200" }} />,
  };
}

interface SmartAlertsWidgetProps {
  sx?: object;
  alerts?: OpsSmartAlert[];
}

export default function SmartAlertsWidget({
  sx,
  alerts = [],
}: SmartAlertsWidgetProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        width: "100%",
        ...sx,
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        color={colors.primary[100]}
        sx={{ mb: 2.5 }}
      >
        Smart Alerts
      </Typography>

      {alerts.length === 0 ? (
        <Typography variant="body2" color={colors.primary[400]} textAlign="center" py={2}>
          No weather alerts for today&apos;s streams
        </Typography>
      ) : (
        <Stack spacing={2}>
          {alerts.map((alert) => {
            const { accentColor, tintBg, icon } = alertStyle(alert);
            return (
              <Box
                key={`${alert.stream_id}-${alert.city}`}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: 2,
                  borderRadius: "16px",
                  backgroundColor: tintBg,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    backgroundColor: "#ffffff",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.04)",
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: accentColor, lineHeight: 1.2 }}
                  >
                    {alert.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{ color: "#555555", display: "block", mt: 0.5, lineHeight: 1.3 }}
                  >
                    {alert.message}
                  </Typography>
                  {alert.temperature !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{ color: accentColor, fontWeight: 600, display: "block", mt: 0.25 }}
                    >
                      {alert.temperature}°C
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
